import apiClient from './api'

export interface RegionPayload {
  id: string
  display_name_en: string
  display_name_ro: string
  sort_order?: number
  is_composite?: boolean
}

export interface TechniquePayload {
  id: string
  category: string
  display_name_en: string
  display_name_ro: string
  applicable_modalities?: string[]
  description?: string
}

export interface SegmentDefinitionPayload {
  code: string
  display_name: string
  display_name_ro: string
  region_id: string
  subregion_id?: string
  laterality?: string
  modality: string
  techniques?: string[]
  purpose_id?: string
}

// Response type pentru listSegmentDefinitions (include id și tenant_id)
export interface SegmentDefinitionResponse {
  id: string
  tenant_id: string
  code: string
  display_name: string
  display_name_ro: string
  region_id: string
  subregion_id?: string
  laterality?: string
  modality: string
  techniques?: string[]
  purpose_id?: string
  status: string
}

export interface MappingRulePayload {
  target_type: string
  target_value: string
  dicom_field: string
  pattern_type: string
  pattern: string
  modality_filter?: string
  confidence_score?: number
  priority?: number
  source?: string
  description?: string
}

export interface SubregionPayload {
  id: string
  parent_region_id: string
  display_name_en: string
  display_name_ro: string
  sort_order?: number
}

export interface LateralityPayload {
  id: string
  display_name_en: string
  display_name_ro: string
  applies_to_regions?: string[]
}

export interface PurposePayload {
  id: string
  display_name_en: string
  display_name_ro: string
  description?: string
}

export const nomenclatorV2Api = {
  async getOverview() {
    const response = await apiClient.get('/nomenclator/v2/overview')
    return response.data
  },

  // Regions
  async listRegions() {
    const response = await apiClient.get('/nomenclator/v2/regions')
    return response.data
  },

  async createRegion(payload: RegionPayload) {
    const response = await apiClient.post('/nomenclator/v2/regions', payload)
    return response.data
  },

  async updateRegion(id: string, payload: Partial<RegionPayload>) {
    const response = await apiClient.put(`/nomenclator/v2/regions/${id}`, payload)
    return response.data
  },

  async deleteRegion(id: string) {
    await apiClient.delete(`/nomenclator/v2/regions/${id}`)
  },

  // Subregions
  async listSubregions(regionId?: string) {
    const params = regionId ? { region_id: regionId } : {}
    const response = await apiClient.get('/nomenclator/v2/subregions', { params })
    return response.data
  },

  async createSubregion(payload: SubregionPayload) {
    const response = await apiClient.post('/nomenclator/v2/subregions', payload)
    return response.data
  },

  async updateSubregion(id: string, payload: Partial<SubregionPayload>) {
    const response = await apiClient.put(`/nomenclator/v2/subregions/${id}`, payload)
    return response.data
  },

  async deleteSubregion(id: string) {
    await apiClient.delete(`/nomenclator/v2/subregions/${id}`)
  },

  // Techniques
  async listTechniques() {
    const response = await apiClient.get('/nomenclator/v2/techniques')
    return response.data
  },

  async createTechnique(payload: TechniquePayload) {
    const response = await apiClient.post('/nomenclator/v2/techniques', payload)
    return response.data
  },

  async updateTechnique(id: string, payload: Partial<TechniquePayload>) {
    const response = await apiClient.put(`/nomenclator/v2/techniques/${id}`, payload)
    return response.data
  },

  async deleteTechnique(id: string) {
    await apiClient.delete(`/nomenclator/v2/techniques/${id}`)
  },

  // Lateralities
  async listLateralities() {
    const response = await apiClient.get('/nomenclator/v2/laterality-options')
    return response.data
  },

  async createLaterality(payload: LateralityPayload) {
    const response = await apiClient.post('/nomenclator/v2/laterality-options', payload)
    return response.data
  },

  async updateLaterality(id: string, payload: Partial<LateralityPayload>) {
    const response = await apiClient.put(`/nomenclator/v2/laterality-options/${id}`, payload)
    return response.data
  },

  async deleteLaterality(id: string) {
    await apiClient.delete(`/nomenclator/v2/laterality-options/${id}`)
  },

  // Purposes
  async listPurposes() {
    const response = await apiClient.get('/nomenclator/v2/clinical-purposes')
    return response.data
  },

  async createPurpose(payload: PurposePayload) {
    const response = await apiClient.post('/nomenclator/v2/clinical-purposes', payload)
    return response.data
  },

  async updatePurpose(id: string, payload: Partial<PurposePayload>) {
    const response = await apiClient.put(`/nomenclator/v2/clinical-purposes/${id}`, payload)
    return response.data
  },

  async deletePurpose(id: string) {
    await apiClient.delete(`/nomenclator/v2/clinical-purposes/${id}`)
  },

  // Segment Definitions
  async listSegmentDefinitions(): Promise<SegmentDefinitionResponse[]> {
    const response = await apiClient.get('/nomenclator/v2/segment-definitions')
    return response.data
  },

  async createSegmentDefinition(payload: SegmentDefinitionPayload) {
    const response = await apiClient.post('/nomenclator/v2/segment-definitions', payload)
    return response.data
  },

  async updateSegmentDefinition(id: string, payload: SegmentDefinitionPayload) {
    const response = await apiClient.put(`/nomenclator/v2/segment-definitions/${id}`, payload)
    return response.data
  },

  async deleteSegmentDefinition(id: string) {
    await apiClient.delete(`/nomenclator/v2/segment-definitions/${id}`)
  },

  // Mapping Rules
  async listMappingRules() {
    const response = await apiClient.get('/nomenclator/v2/mapping-rules')
    return response.data
  },

  async createMappingRule(payload: MappingRulePayload) {
    const response = await apiClient.post('/nomenclator/v2/mapping-rules', payload)
    return response.data
  },

  async updateMappingRule(id: string, payload: Partial<MappingRulePayload>) {
    const response = await apiClient.put(`/nomenclator/v2/mapping-rules/${id}`, payload)
    return response.data
  },

  async deleteMappingRule(id: string) {
    await apiClient.delete(`/nomenclator/v2/mapping-rules/${id}`)
  }
}

